import { motion } from 'framer-motion';
import { Users, Zap, Target, Shield, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fadeUp, staggerContainer, slideInLeft, defaultViewport } from '../components/ui/AnimationVariants';

const values = [
  {
    icon: Shield,
    title: 'Safety on Every Site',
    description: 'Site-specific safety plans, daily toolbox talks, and OSHA-compliant documentation. Our field supervisors enforce safety standards the same way they enforce schedule milestones.',
  },
  {
    icon: Target,
    title: 'Field Accountability',
    description: 'Licensed foremen with direct crew authority. They attend GC coordination meetings, manage daily manpower, and escalate issues before they affect the schedule.',
  },
  {
    icon: Users,
    title: 'Consistent Manpower',
    description: 'We do not ramp up and disappear. We schedule manpower three weeks out and commit to the headcount. GCs can plan around our crew presence.',
  },
  {
    icon: Clock,
    title: 'Schedule Discipline',
    description: 'We build and maintain a three-week lookahead, flag material lead time conflicts early, and adjust when the site schedule shifts — without waiting to be asked.',
  },
];

const milestones = [
  { year: '2008', title: 'Founded',             description: 'Started in residential and light commercial electrical work in the greater Boston area.' },
  { year: '2012', title: 'First Major Contract', description: 'Completed our first multi-million dollar commercial project — a turning point in project scale and crew capacity.' },
  { year: '2016', title: 'Solar Scope Added',    description: 'Expanded into PV system design and installation, initially on residential projects and later on commercial rooftop systems.' },
  { year: '2020', title: 'Team Expansion',       description: 'Grew to 50+ field and office staff to handle simultaneous multi-project workloads.' },
  { year: '2024', title: '500+ Projects',        description: 'Passed 500 completed projects across Massachusetts, with an active backlog of commercial and residential work.' },
];

const About = () => (
  <div>
    {/* Hero */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="section-standard bg-navy-950"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">About BPR Electric</motion.p>
        <motion.h1 variants={fadeUp} className="font-heading text-h1 text-white mt-4 max-w-3xl">
          About BPR Electric
        </motion.h1>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          Commercial electrical contractor based in Massachusetts. We self-perform work on commercial,
          mixed-use, and residential projects — managing the full scope from preconstruction through
          final inspection.
        </motion.p>
      </div>
    </motion.section>

    {/* Company Story */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-900"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <motion.div variants={slideInLeft}>
            <p className="eyebrow">Our Story</p>
            <h2 className="font-heading text-h2 text-white mt-4">Our Story</h2>
            <p className="text-slate-400 text-lg leading-relaxed mt-6">
              BPR Electric was established in 2008 to serve commercial developers and general contractors
              in the Massachusetts market. We started doing residential and small commercial work and grew
              our capacity as we took on larger and more complex projects.
            </p>
            <p className="text-slate-400 text-lg leading-relaxed mt-4">
              Today, our project history includes Class-A office buildings, mixed-use retail centers,
              multi-family apartment complexes, luxury residential developments, and neighborhood electrical
              infrastructure. We self-perform all core electrical work — rough-in, gear installation, and
              finish — with our own licensed foremen and field crews.
            </p>
            <p className="text-slate-400 text-lg leading-relaxed mt-4">
              Our growth has been driven by repeat work from GCs and developers who value direct
              communication, consistent manpower, and a project manager who picks up the phone. We are
              not a large regional utility contractor. We are a focused commercial electrical team that
              handles projects at the right scale and runs them correctly.
            </p>
            <Link to="/projects" className="btn-primary inline-flex items-center mt-8">
              View Our Projects
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
          <motion.div
            variants={fadeUp}
            className="bg-navy-700 border border-navy-600 p-12 flex items-center justify-center"
          >
            <Zap className="h-32 w-32 text-primary-500" />
          </motion.div>
        </div>
      </div>
    </motion.section>

    {/* How We Operate */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-950"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">How We Operate</motion.p>
        <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
          How We Operate
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          The operational standards BPR Electric holds across every project.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 mt-16">
          {values.map((value, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="border-t border-navy-600 pt-8 pb-8 pr-8"
            >
              <value.icon className="h-6 w-6 text-primary-500 mb-6" />
              <h3 className="font-heading text-white uppercase text-lg">{value.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed mt-4">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>

    {/* Timeline */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-900"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">Our Journey</motion.p>
        <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
          Our Journey
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          Key milestones that have shaped our company into what it is today.
        </motion.p>

        <div className="mt-16 space-y-0">
          {milestones.map((milestone, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="grid grid-cols-[80px_1fr] gap-8 border-t border-navy-600 py-8"
            >
              <div className="font-heading text-h3 text-primary-500">{milestone.year}</div>
              <div>
                <h3 className="font-heading text-white uppercase text-xl">{milestone.title}</h3>
                <p className="text-slate-400 leading-relaxed mt-2">{milestone.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>

    {/* CTA */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-950"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">Get Started</motion.p>
        <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4 max-w-3xl">
          Working on a Commercial Project in Massachusetts?
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          We are available to discuss your project scope, timeline, and crew requirements. No pitch
          — a direct conversation about whether it's a fit.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-10">
          <Link to="/contact" className="btn-primary inline-flex items-center">
            Talk to Our Team
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </motion.div>
      </div>
    </motion.section>
  </div>
);

export default About;
